import React, { useState, useEffect, useMemo } from 'react';
import { X, DollarSign, Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Popup that opens when an admin flips a student's funding status to
 * "Funding Claimed". Captures per-year claim amounts, then calls the
 * backend which computes the recruiter's commission based on the
 * cumulative tier ratchet and notifies the recruiter to invoice.
 */
const FundingClaimModal = ({ student, colleges, onClose, onSaved }) => {
  const { toast } = useToast();
  const college = (colleges || []).find((c) => c.id === student.assigned_college);
  const program = (college?.programs || []).find((p) => p.id === student.assigned_program_id);
  const defaultYears = Math.max(1, Number(program?.duration_years || 1));

  const initial = useMemo(() => {
    if (student.funding_claims?.length) {
      return student.funding_claims.map((c) => ({ year: c.year, amount: c.amount }));
    }
    return Array.from({ length: defaultYears }, (_, i) => ({ year: i + 1, amount: 0 }));
  }, [student.id, defaultYears]); // eslint-disable-line

  const [claims, setClaims] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const updateYear = (idx, amount) => {
    const next = [...claims];
    next[idx] = { ...next[idx], amount };
    setClaims(next);
  };

  const addYear = () => {
    const nextYear = (claims.at(-1)?.year || 0) + 1;
    setClaims([...claims, { year: nextYear, amount: 0 }]);
  };

  const removeYear = (idx) => {
    if (claims.length <= 1) return;
    setClaims(claims.filter((_, i) => i !== idx));
  };

  const total = claims.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = claims
      .map((c) => ({ year: parseInt(c.year, 10), amount: parseFloat(c.amount) || 0 }))
      .filter((c) => c.amount > 0);
    if (!cleaned.length) {
      toast({ title: 'Enter at least one amount', description: 'Add the claim amount for each year.' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/students/${student.id}/funding-claim`, { claims: cleaned });
      const amt = Number(data.commission_total || 0).toLocaleString();
      toast({
        title: 'Funding claimed — commission unlocked',
        description: `Recruiter earned $${amt} at ${data.percent_applied}%. Invoice notification sent.`,
      });
      onSaved?.();
    } catch (err) {
      toast({ title: 'Failed to save claim', description: err?.response?.data?.detail || 'Error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl pointer-events-auto">
          <div className="sticky top-0 bg-white z-10 border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Funding Claimed</div>
              <h3 className="font-display text-xl text-[#0B5E2A]">{student.full_name}</h3>
              {college && (
                <div className="text-xs text-[#6b7e6d] mt-0.5">
                  {college.name}{program ? ` · ${program.name}` : ''}
                </div>
              )}
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]" data-testid="funding-claim-close">
              <X className="h-5 w-5 pointer-events-none" />
            </button>
          </div>
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-[#4b5e4d]">
              Enter how much {college?.name || 'the college'} is claiming per academic year. The recruiter's commission will be computed using their current tier ratchet and they'll be notified to raise an invoice.
            </p>
            <div className="rounded-lg border border-[#F0B93C]/40 bg-[#FEF8E6] p-4 space-y-3">
              <div className="flex items-center gap-2 text-[#8a5e00] text-sm font-semibold">
                <DollarSign className="h-4 w-4" /> Per-Year Claims
              </div>
              <div className="space-y-2">
                {claims.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#8a5e00] w-16">Year {c.year}</span>
                    <div className="flex-1 flex items-center gap-2">
                      <span className="text-[#8a5e00]">$</span>
                      <input
                        type="number"
                        min="0"
                        step="50"
                        value={c.amount}
                        onChange={(e) => updateYear(i, e.target.value)}
                        data-testid={`funding-claim-year-${c.year}`}
                        className="flex-1 px-3 py-2 rounded-md border border-[#F0B93C]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#F0B93C]/30 bg-white"
                      />
                    </div>
                    {claims.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeYear(i)}
                        className="h-8 w-8 rounded border border-[#F0B93C]/40 text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center"
                        title="Remove year"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addYear}
                data-testid="funding-claim-add-year"
                className="text-xs font-semibold text-[#0B5E2A] hover:underline inline-flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add another year
              </button>
              <div className="rounded-md bg-white border border-[#F0B93C]/40 px-3 py-2 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#6b7e6d]">Total Claim</span>
                <span className="font-display text-lg font-bold text-[#0B5E2A]" data-testid="funding-claim-total">
                  ${Number(total).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex gap-2 pt-2 border-t border-[#0B5E2A]/10">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || total <= 0}
                data-testid="funding-claim-submit"
                className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>) : (<><Check className="h-4 w-4 pointer-events-none" /> Save & Unlock Commission</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default FundingClaimModal;
